import { useState } from 'react';
import { Card, Descriptions, Button, message, Space, Modal } from 'antd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  getApp, 
  resetIOSKey, 
  resetAndroidKey 
} from '@/api/apps';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { SecretDisplayModal } from '@/components/ui/SecretDisplayModal';
import { getCurrentDeveloperId } from '@/stores/authStore';
// 导入图标
import { ArrowLeftOutlined, ReloadOutlined, WarningOutlined } from '@ant-design/icons';

/**
 * App 密钥管理页面
 */
export const AppKeysManagement = () => {
  const { appId } = useParams<{ appId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const developerId = getCurrentDeveloperId();

  // 查询 App 详情
  const appQuery = useQuery({
    queryKey: ['app', developerId, appId],
    queryFn: () => {
      if (!appId) {
        throw new Error('App ID 不能为空');
      }
      return getApp(appId);
    },
    enabled: !!appId && !!developerId,
  });

  const app = appQuery.data;
  const loading = appQuery.isLoading;
  const error = appQuery.error;

  // Modal 状态
  const [secretModalOpen, setSecretModalOpen] = useState<boolean>(false);
  const [newKeys, setNewKeys] = useState<{ ios: string; android: string } | null>(null);

  /**
   * 处理密钥重置
   */
  const handleRegenerateKeys = async (): Promise<void> => {
    if (!appId) return;

    Modal.confirm({
      title: '确认重置所有密钥',
      icon: <WarningOutlined className="text-orange-500" />,
      content: '确定要重置 iOS + Android 密钥吗？\n此操作将使现有 App Secret 失效，需重新集成！',
      okText: '确认重置',
      cancelText: '取消',
      onOk: async () => {
        try {
          const [iosResponse, androidResponse] = await Promise.all([
            resetIOSKey(appId),
            resetAndroidKey(appId)
          ]);
          
          setNewKeys({ ios: iosResponse.app_secret, android: androidResponse.app_secret });
          setSecretModalOpen(true);
          await queryClient.invalidateQueries({ queryKey: ['app', developerId, appId] });
          message.success('密钥重置成功');
        } catch {
          message.error('密钥重置失败，请重试。');
        }
      }
    });
  };

  /**
   * 处理返回 App 详情
   */
  const handleBack = (): void => {
    if (appId) {
      navigate(`/apps/${appId}`);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <ErrorAlert 
        message="加载失败" 
        description="无法加载密钥信息，请检查网络或稍后重试。"
        className="mb-8"
      />
    );
  }

  if (!app) {
    return (
      <ErrorAlert 
        message="未找到 App" 
        description="指定的 App 不存在或已被删除"
        className="mb-8"
      />
    );
  }

  // 检查是否有密钥信息
  const hasIOSKey = app.ios_key !== undefined;
  const hasAndroidKey = app.android_key !== undefined;

  return (
    <div className="w-full space-y-8">
      {/* 标题+操作区 */}
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">密钥管理</h1>
          <p className="text-gray-500 mt-1">管理App的iOS/Android密钥，重置后需更新客户端配置。</p>
        </div>
        <Space size="middle">
          <Button 
            onClick={handleBack}
            icon={<ArrowLeftOutlined />}
            className="hover:bg-gray-50 transition-colors"
          >
            返回详情
          </Button>
          <Button 
            danger 
            onClick={handleRegenerateKeys}
            icon={<ReloadOutlined />}
            className="px-6 hover:shadow-md transition-shadow"
          >
            重置所有密钥
          </Button>
        </Space>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* iOS 密钥 */}
        <Card 
          title="iOS 密钥"
          className="border-0 shadow-sm hover:shadow-md transition-shadow"
          style={{ 
            padding: '24px',
            '--ant-card-head-font-size': '16px',
            '--ant-card-head-font-weight': 600
          } as React.CSSProperties}
        >
          {hasIOSKey ? (
            <Descriptions
              column={1}
              bordered={false}
              labelStyle={{ width: '120px', fontWeight: 600, color: '#667085' }}
              contentStyle={{ color: '#1D2129' }}
              style={{ fontSize: '15px' } as React.CSSProperties}
            >
              <Descriptions.Item label="App Key">
                <span className="font-mono text-blue-600">{app.ios_key!.app_key}</span>
              </Descriptions.Item>
              <Descriptions.Item label="Bundle ID">
                <span className="font-mono">{app.ios_key!.bundle_id}</span>
              </Descriptions.Item>
              <Descriptions.Item label="创建时间">
                {new Date(app.ios_key!.created_at).toLocaleString().split(' ')[0]}
              </Descriptions.Item>
            </Descriptions>
          ) : (
            <div className="text-center py-8 text-gray-500">
              暂无 iOS 密钥信息，创建App时自动生成。
            </div>
          )}
        </Card>

        {/* Android 密钥 */}
        <Card 
          title="Android 密钥"
          className="border-0 shadow-sm hover:shadow-md transition-shadow"
          style={{ 
            padding: '24px',
            '--ant-card-head-font-size': '16px',
            '--ant-card-head-font-weight': 600
          } as React.CSSProperties}
        >
          {hasAndroidKey ? (
            <Descriptions
              column={1}
              bordered={false}
              labelStyle={{ width: '120px', fontWeight: 600, color: '#667085' }}
              contentStyle={{ color: '#1D2129' }}
              style={{ fontSize: '15px' } as React.CSSProperties}
            >
              <Descriptions.Item label="App Key">
                <span className="font-mono text-blue-600">{app.android_key!.app_key}</span>
              </Descriptions.Item>
              <Descriptions.Item label="包名">
                <span className="font-mono">{app.android_key!.bundle_id}</span>
              </Descriptions.Item>
              <Descriptions.Item label="创建时间">
                {new Date(app.android_key!.created_at).toLocaleString().split(' ')[0]}
              </Descriptions.Item>
            </Descriptions>
          ) : (
            <div className="text-center py-8 text-gray-500">
              暂无 Android 密钥信息，创建App时自动生成。
            </div>
          )}
        </Card>
      </div>

      {/* 密钥展示模态框 */}
      <SecretDisplayModal
        open={secretModalOpen}
        iosSecret={newKeys?.ios || ''}
        androidSecret={newKeys?.android || ''}
        onClose={() => setSecretModalOpen(false)}
      />
    </div>
  );
};